import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import TimelineRenderer from "@/components/timeline/TimelineRenderer";
import TimeNavigator from "@/components/timeline/TimeNavigator";
import CommentsThread from "@/components/timeline/CommentsThread";
import { SlashMark } from "@/components/Logo";
import { api, apiErr, copyText } from "@/lib/api";
import { themeOf } from "@/lib/themes";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Share2, Play } from "lucide-react";

export default function PublicTimeline() {
  const { username, slug } = useParams();
  const [tl, setTl] = useState(null);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("All");
  const [counts, setCounts] = useState({});
  const [commentEvent, setCommentEvent] = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/public/${username}/${slug}`); setTl(data); }
      catch (e) { setErr(apiErr(e)); }
    })();
  }, [username, slug]);

  const loadCounts = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/timelines/${id}/comments`);
      const c = {};
      data.forEach((cm) => { if (cm.event_id) c[cm.event_id] = (c[cm.event_id] || 0) + 1; });
      setCounts(c);
    } catch {}
  }, []);

  useEffect(() => { if (tl?.id) loadCounts(tl.id); }, [tl, loadCounts]);

  if (err) return (
    <div className="min-h-screen grid place-items-center bg-background text-center px-6">
      <div>
        <SlashMark size={40} color="hsl(var(--primary))" className="mx-auto mb-4" />
        <h1 className="font-display text-2xl font-semibold text-foreground">Timeline not found</h1>
        <p className="mt-2 text-muted-foreground">{err}</p>
        <Link to="/discover"><Button className="mt-6 rounded-full">Explore timelines</Button></Link>
      </div>
    </div>
  );
  if (!tl) return <div className="min-h-screen grid place-items-center bg-background"><div className="animate-pulse"><SlashMark size={40} color="hsl(var(--primary))" /></div></div>;

  const t = themeOf(tl.theme);
  const showBrand = tl.settings?.show_branding !== false;
  const showNav = tl.settings?.show_navigator !== false && tl.layout !== "fullscreen" && (tl.events?.length || 0) > 1;

  return (
    <div className="min-h-screen relative" style={{ background: t.bg }}>
      <div className="min-h-screen">
        <TimelineRenderer timeline={tl} filter={{ q, category }} onComment={setCommentEvent} commentCounts={counts} />
      </div>

      {/* Floating controls */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2">
        <Link to={`/u/${username}`} data-testid="public-author" className="glass h-10 px-4 rounded-full text-sm font-medium flex items-center gap-1.5" style={{ color: t.text }}>@{username}</Link>
        <a href={`/present/${username}/${slug}`} target="_blank" rel="noreferrer" data-testid="public-present"
          className="glass h-10 px-4 rounded-full text-sm font-medium flex items-center gap-1.5" style={{ color: t.text }}>
          <Play size={15} /> Present
        </a>
        <button data-testid="public-share"
          onClick={() => { copyText(window.location.href); toast.success("Link copied"); }}
          className="glass h-10 px-4 rounded-full text-sm font-medium flex items-center gap-1.5" style={{ color: t.text }}>
          <Share2 size={15} /> Share
        </button>
      </div>

      {showNav && <TimeNavigator timeline={tl} t={t} q={q} setQ={setQ} category={category} setCategory={setCategory} />}

      {showBrand && (
        <Link to="/" data-testid="made-with-badge"
          className={`fixed ${showNav ? "bottom-4 left-4" : "bottom-4 right-4"} z-50 glass h-9 px-3.5 rounded-full text-xs font-medium flex items-center gap-1.5`} style={{ color: t.text }}>
          <SlashMark size={12} color={t.accent} /> Made with Timeline
        </Link>
      )}

      <Sheet open={!!commentEvent} onOpenChange={(o) => { if (!o) { setCommentEvent(null); loadCounts(tl.id); } }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="comment-sheet">
          {commentEvent && (
            <>
              <SheetHeader>
                <div className="text-xs uppercase tracking-widest text-primary">{commentEvent.date}</div>
                <SheetTitle className="font-display text-2xl text-left">{commentEvent.title}</SheetTitle>
                <SheetDescription className="text-left">Join the conversation about this moment.</SheetDescription>
              </SheetHeader>
              <div className="mt-6"><CommentsThread timelineId={tl.id} eventId={commentEvent.id} /></div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
